import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import { loginThunk, logoutThunk, refreshSessionThunk } from '../store/auth/authThunks';
import {
  selectUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectAuthError,
  selectUserRole,
  selectHasRole,
  selectHasAnyRole,
  selectHasMinimumRole,
} from '../store/auth/authSelectors';
import { clearError } from '../store/auth/authSlice';
import { LoginCredentials, UserRole } from '../types/UserInterface';

// ─── Main auth hook ───────────────────────────────────────────────────────────
export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();

  return {
    user:            useSelector(selectUser),
    isAuthenticated: useSelector(selectIsAuthenticated),
    loading:         useSelector(selectAuthLoading),
    error:           useSelector(selectAuthError),
    role:            useSelector(selectUserRole),
    isAdmin:         useSelector(selectHasRole('Admin')),
    isManager:       useSelector(selectHasAnyRole('Admin', 'Manager')),
    isStaff:         useSelector(selectHasAnyRole('Admin', 'Manager', 'Staff')),
    login:           (credentials: LoginCredentials) => dispatch(loginThunk(credentials)),
    logout:          () => dispatch(logoutThunk()),
    refreshSession:  () => dispatch(refreshSessionThunk()),
    clearError:      () => dispatch(clearError()),
  };
};

// ─── Separate hook for minimum role check ────────────────────────────────────
// Usage:  const isSenior = useHasMinimumRole('Manager');
export const useHasMinimumRole = (role: UserRole): boolean =>
  useSelector(selectHasMinimumRole(role));